import {
    Observable, BehaviorSubject, Subject,
    distinctUntilChanged
} from "rxjs";

import {
    inject,
    Injectable, OnDestroy
} from "@angular/core";
import { IAppService } from "../interfaces/app-service";
import { AuthenticationService } from "../../iapi-application/services/authentication.service";
import { NotificationService } from "../../notification/services/notification.service";
import { LoggingService } from "../../services/logging/logging.service";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import { ICommonConfig } from "../interfaces/common-config";
import { IAppRouterService } from "../interfaces/app-router-service";

/**
 * Defines functions and fields common to all ApplicationStateServices
 *
 * Each application has its own AppService which implements the IAppService interface
 * by extending AbstractAppService
 *
 * ```
 * @Injectable()
 * export class AppService extends AbstractAppService<IAppConfig, AppRouterService>{
 *
 * }
 *````
 * The Application state service manages the global state of the application.
 * For example processing or fullscreen
 *
 */
@Injectable()
export abstract class AbstractAppService<C extends ICommonConfig, R extends IAppRouterService>
implements IAppService<C, R>, OnDestroy {

    readonly authService = inject(AuthenticationService);
    readonly errorHandler = inject(COMMON_ERROR_HANDLER);
    readonly logger = inject(LoggingService);
    readonly notificationService = inject(NotificationService);
    private readonly _processing$ = new BehaviorSubject<boolean>(false);
    private readonly _fullscreen$ = new BehaviorSubject<boolean>(false);
    private readonly _closeAllPanels$ = new Subject<void>();

    abstract readonly config: C;
    abstract readonly appRouter: R;

    get fullscreen$(): Observable<boolean> {

        return this._fullscreen$.asObservable().pipe(distinctUntilChanged());
    }

    get fullscreen(): boolean {

        return this._fullscreen$.value;
    }

    get processing$(): Observable<boolean> {

        return this._processing$.asObservable().pipe(distinctUntilChanged());
    }

    get processing(): boolean {
        return this._processing$.value;
    }

    ngOnDestroy(): void {
        this._closeAllPanels$.complete();
        this._fullscreen$.complete();
        this._processing$.complete();
    }

    fullscreenOff(): void {

        this._fullscreen$.next(false);
    }

    fullscreenOn(): void {

        this._fullscreen$.next(true);
    }

    fullscreenToggle(): void {

        this._fullscreen$.next(!this.fullscreen);
    }

    processingStart(): void {

        this._processing$.next(true);
    }

    processingStop(): void {
        this._processing$.next(false);
    }

    get closeAllPanels$(): Observable<void> {
        return this._closeAllPanels$.asObservable();
    }

    closeAllPanels(): void {
        this._closeAllPanels$.next();
    }

}
